import { useEffect, useMemo, useRef, useState } from "react";
import {
    format,
    startOfToday,
    subDays,
    addDays,
    addMonths,
    subMonths,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
} from "date-fns";
import { ReactComponent as ArrowLeft } from "../../console/assets/icons/arrowLeft.svg";
import { ReactComponent as LeftArrow } from "../../console/assets/icons/leftArrow.svg";
import { ReactComponent as RightArrow } from "../../console/assets/icons/rightArrow.svg";
import { ReactComponent as Refresh } from "../../console/assets/icons/refresh.svg";
import { ReactComponent as Filter } from "../../console/assets/icons/filter.svg";
import { ReactComponent as Alarm } from "../../console/assets/icons/alarm.svg";
import { ReactComponent as CloseBlackIcon } from "../../console/assets/icons/closeIcon.svg";
import { ReactComponent as SelectIconExcel } from "../../common/assets/icons/selectIcon.svg";
import { ReactComponent as SelectIcon } from "../../common/assets/icons/selectIcon.svg";
import Input from "../../console/components/common/input";
import SelectBox from "../../console/components/common/selectBox";
import Button from "../../common/components/button";
import Spinner from "../../common/components/spinner";
import BranchLayout from "../-components/BranchLayout";
import BranchTable from "../-components/BranchTable";
import BranchFilterDetail from "../-components/BranchFilterDetail";
import BranchExcel from "../-components/BranchExcel";
import BranchOrderDetail from "../-components/BranchOrderDetail";
import BranchAlarmPop from "../-components/BranchAlarmPop";
import { useGetOrder } from "../../common/hooks/console/transaction/useGetOrder";
import { useGetOrderCount } from "../../common/hooks/console/transaction/useGetOrderCount";
import { useGetOrderDateCount } from "../../common/hooks/console/transaction/useGetOrderDateCount";
import { usePutOrderStatus } from "../../common/hooks/console/transaction/usePutOrderStatus";
import { usePutReceipt } from "../../common/hooks/console/transaction/usePutReceipt";
import { usePutBranchDelete } from "../../common/hooks/console/transaction/usePutBranchDelete";
import { getBase64Image, printReceipt } from "../-components/printReceipt";
import receiptLogo from "../../console/assets/images/receiptLogo.png";
import useToastStore from "../../console/store/useToastStore";
import { useBranchOrderAlert } from "../-components/useBranchOrderAlert";
import { useSystemPopStore } from "../../console/store/useSystemPopStore";

/**
 * 지점 포털 - 주문판매
 *
 * 본점 콘솔 Transaction 화면과 동일한 레이아웃. 지점이 쓸 수 없는 것만 뺐다.
 *  - 합계 금액 : 판매금액이 마스킹돼 내려오므로 건수만 표시
 *  - 신규주문 등록 / 엑셀 / 인수증 / 매출통계 / 알림 / 삭제건 보기 : 1계층이 403 차단
 *  - 주문취소 / 보류 / 삭제 : 2계층이 400 차단
 *  - 발주상태(본점/타지점) 필터 : 지점은 자기 발주 건만 보므로 무의미
 *
 * 발주처 스코프는 서버(branch-scope)가 강제하므로 화면에서 지점 조건을 보내지 않는다.
 */
// 상단 카운트 - 콘솔과 동일하게 삭제(7) 제외 전체
const STATUS_COUNT_ITEMS = [
    { value: 1, label: "미확인" },
    { value: 2, label: "접수완료" },
    { value: 3, label: "준비완료" },
    { value: 4, label: "배송완료" },
    { value: 5, label: "취소" },
    { value: 6, label: "보류" },
];

// 지점이 지정할 수 있는 상태 (백엔드 branch-ownership 과 동일)
const STATUS_CHANGE_OPTIONS = [
    { value: 2, label: "접수완료" },
    { value: 3, label: "준비완료" },
    { value: 4, label: "배송완료" },
];

// 콘솔과 동일. 삭제(7) 상태만 제외한 전체
const DEFAULT_ORDER_STATUS = [1, 2, 3, 4, 5, 6];

/* 신규 발주 배지 기준 시각 보관 키. 서버에 읽음 상태가 없어 브라우저에 남긴다 */
const ALARM_SEEN_KEY = "branchAlarmSeenAt";

const SEARCH_OPTIONS = [
    { value: "all_search", label: "전체검색" },
    { value: "order_name", label: "주문자" },
    { value: "recipient_name", label: "받으시는 분" },
    { value: "delivery_address", label: "배송주소" },
    { value: "order_number", label: "배송일자명" },
    { value: "product_name", label: "상품명" },
];

const OrderList = () => {
    const { addToast } = useToastStore();
    const { showPop } = useSystemPopStore();
    const statusRef = useRef<HTMLDivElement>(null);

    // 콘솔과 동일하게 초기에는 날짜 필터를 걸지 않는다 (전체 기간)
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedQuickFilter, setSelectedQuickFilter] = useState<string | null>(null);
    const [orderStatusList, setOrderStatusList] = useState<number[]>(DEFAULT_ORDER_STATUS);
    const [filterClick, setFilterClick] = useState(false);
    const [excelClick, setExcelClick] = useState(false);
    const [alarmPop, setAlarmPop] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    /* 신규 발주 배지 기준 시각. 종을 눌러 확인하면 갱신한다 (브라우저별 보관) */
    const [alarmSeenAt, setAlarmSeenAt] = useState<string>(() => localStorage.getItem(ALARM_SEEN_KEY) || "");
    // 상세 패널 접기/펴기. 탭은 그대로 두고 화면만 감춘다
    const [detailOpen, setDetailOpen] = useState(true);
    // 열려 있는 상세 탭의 refetch 함수. 새로고침 때 목록과 함께 다시 불러온다
    const [detailRefetchMap, setDetailRefetchMap] = useState<Record<number, () => Promise<any>>>({});
    const [tabs, setTabs] = useState<{ id: number; orderNumber?: string }[]>([]);
    const [activeTabId, setActiveTabId] = useState<number | null>(null);
    const [search, setSearch] = useState("all_search");
    const [searchTxt, setSearchTxt] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [inputValue, setInputValue] = useState("1");
    const [pageSize, setPageSize] = useState(50);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [clearSelection, setClearSelection] = useState(false);
    const [changeStatus, setChangeStatus] = useState(false);
    const [sortField, setSortField] = useState("");
    const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

    const filterData = useMemo(
        () => ({
            start_date: startDate,
            end_date: endDate,
            order_status: orderStatusList,
            payment_status: [],
            branch_type: [],
        }),
        [startDate, endDate, orderStatusList],
    );

    const {
        data: listData,
        isLoading,
        refetch: listRefetch,
    } = useGetOrder(currentPage, pageSize, search, searchTxt, filterData, sortField, sortOrder);
    const { data: countData, refetch: countRefetch } = useGetOrderCount({
        start_date: startDate,
        end_date: endDate,
        branch_since: alarmSeenAt || undefined,
    });
    const { data: dateData, refetch: dateRefetch } = useGetOrderDateCount(startDate, endDate, filterData);

    const { mutate: putStatus, isPending: isStatusPending } = usePutOrderStatus(() => {
        setSelectedIds([]);
        setClearSelection(true);
        countRefetch();
        dateRefetch();
        addToast({ type: "success", message: "주문 상태를 변경했습니다." });
    });

    const { mutate: receiptMutate, isPending: isReceiptPending } = usePutReceipt(
        data => {
            getBase64Image(receiptLogo, base64Logo => printReceipt(data, base64Logo));
            listRefetch();
        },
        error => {
            addToast({ type: "error", message: error?.message || "인수증 출력에 실패했습니다." });
        },
    );

    const { mutate: branchDelete, isPending: isDeletePending } = usePutBranchDelete(() => {
        setSelectedIds([]);
        setClearSelection(true);
        countRefetch();
        dateRefetch();
        addToast({ type: "success", message: "선택한 주문을 목록에서 삭제했습니다." });
    });

    const lastPage = listData?.last_page || 1;
    const latestBranchDate = countData?.branch_latest_date ?? null;
    const newOrderCount = countData?.branch_new_count || 0;

    // 신규 발주 음성 알림 - 발주 시각을 1분마다 다시 확인한다
    useBranchOrderAlert(countData ? latestBranchDate : undefined, newOrderCount);

    /* 종을 눌러 확인하면 그 시점의 마지막 발주 시각을 기준으로 잡아 배지를 지운다 */
    const handleAlarmToggle = () => {
        setAlarmPop(prev => {
            const next = !prev;
            if (next && latestBranchDate) {
                setAlarmSeenAt(latestBranchDate);
                localStorage.setItem(ALARM_SEEN_KEY, latestBranchDate);
            }
            return next;
        });
    };

    useEffect(() => {
        const timer = setInterval(() => countRefetch(), 60 * 1000);
        return () => clearInterval(timer);
    }, [countRefetch]);

    const handleClickOutside = (event: MouseEvent) => {
        if (statusRef.current && !statusRef.current.contains(event.target as Node)) {
            setChangeStatus(false);
        }
    };

    useEffect(() => {
        if (changeStatus) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [changeStatus]);

    const resetPaging = () => {
        setCurrentPage(1);
        setInputValue("1");
        setSelectedIds([]);
        setClearSelection(true);
    };

    /* 같은 버튼을 다시 누르면 기간 필터 해제. 본점 Transaction.tsx 와 동일한 토글 동작 */
    const handleQuickFilterClick = (key: string, start: string, end: string) => {
        if (selectedQuickFilter === key) {
            setSelectedQuickFilter(null);
            setStartDate("");
            setEndDate("");
        } else {
            setSelectedQuickFilter(key);
            setStartDate(start);
            setEndDate(end);
        }
        /*
        // 기간을 바꿀 때는 주문상태를 전체로 되돌린다.
        // 특정 상태만 보고 있던 상태에서 기간만 바꾸면 조건이 겹쳐
        // 해당 기간에 건이 있는데도 비어 보인다.
        */
        setOrderStatusList(DEFAULT_ORDER_STATUS);
        resetPaging();
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setInputValue(page.toString());
        setSelectedIds([]);
        setClearSelection(true);
    };

    const handlePageInput = (value: string) => {
        setInputValue(value);
        const pageNumber = Number(value);
        if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > lastPage) return;
        setCurrentPage(pageNumber);
    };

    // 카운트 클릭 - 해당 상태만 보기 / 다시 누르면 전체
    const isSingleStatus = (value: number) => orderStatusList.length === 1 && orderStatusList[0] === value;

    const handleOrderStatusClick = (value: number) => {
        setOrderStatusList(prev => (prev.length === 1 && prev[0] === value ? DEFAULT_ORDER_STATUS : [value]));
        resetPaging();
    };

    const handleApplyFilter = (applied: { start_date: string; end_date: string; order_status: number[] }) => {
        setStartDate(applied.start_date);
        setEndDate(applied.end_date);
        setOrderStatusList(applied.order_status);
        setSelectedQuickFilter(null);
        resetPaging();
    };

    const handleStatusSubmit = (order_status: number) => {
        if (selectedIds.length === 0) return;
        putStatus(
            { order_idx: selectedIds, order_status },
            {
                onError: error => {
                    addToast({
                        type: "error",
                        message: error.response?.data?.message || "주문 상태 변경에 실패했습니다.",
                    });
                },
            },
        );
    };

    const handlePrintReceipt = () => {
        if (selectedIds.length === 0) return;
        receiptMutate({ order_idx: selectedIds });
    };

    const handleBranchDelete = () => {
        if (selectedIds.length === 0) return;

        // 해제는 본점만 가능하므로 되돌리기 어렵다. 반드시 확인을 받는다
        showPop({
            title: `선택한 ${selectedIds.length}건을 삭제하시겠습니까?`,
            content: "목록에서만 숨겨지며, 되돌리려면 본점에 요청해야 합니다.",
            rightButton: "삭제",
            type: "negative",
            onRightClick: () => {
                branchDelete(
                    { order_idx: selectedIds, branch_del: "Y" },
                    {
                        onError: error => {
                            addToast({
                                type: "error",
                                message: error.response?.data?.message || "삭제에 실패했습니다.",
                            });
                        },
                    },
                );
            },
        });
    };

    // 상세 패널 - 콘솔과 동일하게 오른쪽에 탭으로 띄운다
    const handleOpenDetail = (orderIdx: number) => {
        setTabs(prev => (prev.some(tab => tab.id === orderIdx) ? prev : [...prev, { id: orderIdx }]));
        setActiveTabId(orderIdx);
        setDetailOpen(true);
    };

    const handleCloseTab = (tabId: number) => {
        // 닫힌 탭의 refetch 가 남아 있으면 새로고침 때 없는 상세를 다시 부른다
        setDetailRefetchMap(prev => {
            if (!(tabId in prev)) return prev;
            const next = { ...prev };
            delete next[tabId];
            return next;
        });
        setTabs(prev => {
            const next = prev.filter(tab => tab.id !== tabId);
            setActiveTabId(current => (current === tabId ? next[next.length - 1]?.id ?? null : current));
            return next;
        });
    };

    /* 본점 Transaction.tsx 와 동일. 목록·카운트와 열려 있는 상세 탭을 함께 다시 불러오고
       응답이 즉시 와도 눌린 게 보이도록 스피너를 최소 100ms 유지한다 */
    const handleRefresh = async () => {
        setIsRefreshing(true);
        const startTime = Date.now();

        const detailRefetchFunctions = Object.values(detailRefetchMap).filter(refetch => typeof refetch === "function");

        await Promise.all([listRefetch(), countRefetch(), dateRefetch(), ...detailRefetchFunctions.map(refetch => refetch())]);

        const remainingTime = Math.max(100 - (Date.now() - startTime), 0);
        setTimeout(() => setIsRefreshing(false), remainingTime);
    };

    const handleSetRefetch = (tabId: number, refetchFn: () => Promise<any>) => {
        setDetailRefetchMap(prev => {
            if (prev[tabId] === refetchFn) return prev;
            return { ...prev, [tabId]: refetchFn };
        });
    };

    const quickFilters = [
        {
            key: "prevMonth",
            label: "전월",
            range: () => [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))],
        },
        { key: "yesterday", label: "전일", range: () => [subDays(new Date(), 1), subDays(new Date(), 1)] },
        { key: "today", label: "오늘", range: () => [startOfToday(), startOfToday()] },
        { key: "tomorrow", label: "내일", range: () => [addDays(new Date(), 1), addDays(new Date(), 1)] },
        {
            key: "week",
            label: "금주",
            range: () => [startOfWeek(new Date(), { weekStartsOn: 1 }), endOfWeek(new Date(), { weekStartsOn: 1 })],
        },
        { key: "month", label: "당월", range: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
        {
            key: "nextMonth",
            label: "익월",
            range: () => [startOfMonth(addMonths(new Date(), 1)), endOfMonth(addMonths(new Date(), 1))],
        },
    ];

    return (
        <BranchLayout>
            <div className="flex h-screen w-full overflow-hidden">
                {(isLoading || isRefreshing || isStatusPending || isReceiptPending || isDeletePending) && <Spinner />}
                <div className="flex flex-1 min-w-0 flex-col">
                    {/* Header */}
                    <div className="flex justify-between px-3 border-b border-gray4 py-3 gap-2 overflow-x-auto">
                        <div className="flex-col flex gap-2">
                            <div className="flex gap-2 items-center">
                                <p className="text-2xl ml-1">주문판매</p>
                                <div className="flex items-center">
                                    <p className="text-sm font-bold ml-4">매출 및 주문상태</p>
                                    <div className="flex gap-1 mx-4">
                                        {quickFilters.map(filter => (
                                            <Button
                                                key={filter.key}
                                                status={selectedQuickFilter === filter.key ? "regist" : undefined}
                                                size="S"
                                                className="px-2"
                                                onClick={() => {
                                                    const [start, end] = filter.range();
                                                    handleQuickFilterClick(
                                                        filter.key,
                                                        format(start, "yyyy-MM-dd"),
                                                        format(end, "yyyy-MM-dd"),
                                                    );
                                                }}
                                            >
                                                {filter.label}
                                            </Button>
                                        ))}
                                    </div>

                                    <p className="flex font-bold items-center gap-2 text-sm text-red-500">
                                        합계
                                        <span>{(dateData?.total_order_amount || 0).toLocaleString()} 원</span>
                                        <span>({(dateData?.total_order_count || 0).toLocaleString()}건)</span>
                                    </p>
                                </div>
                            </div>

                            <div className="bg-gray6 py-2 px-6 rounded-[20px] flex gap-4 items-center">
                                <div className="flex gap-4 items-center">
                                    <div className="gap-3 flex">
                                        <p className="text-sm font-bold">주문상태</p>
                                        <div className="flex items-center gap-2">
                                            {STATUS_COUNT_ITEMS.map(({ label, value }) => {
                                                const statusCount =
                                                    countData?.order.find(order => order.order_status === value)?.count || 0;

                                                return (
                                                    <div key={value} className="flex items-center gap-1">
                                                        <p
                                                            className={`text-xs ${isSingleStatus(value) ? "font-bold" : ""} ${
                                                                label === "미확인" && "text-warning"
                                                            }`}
                                                        >
                                                            {label}
                                                        </p>
                                                        <button
                                                            style={{ fontWeight: isSingleStatus(value) ? "bold" : "normal" }}
                                                            className={`text-sm underline ${
                                                                label === "미확인"
                                                                    ? "text-warning"
                                                                    : isSingleStatus(value)
                                                                      ? "text-[#00489D]"
                                                                      : "text-primary2"
                                                            }`}
                                                            onClick={() => handleOrderStatusClick(value)}
                                                        >
                                                            {statusCount.toLocaleString()}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}>
                                <LeftArrow />
                            </button>
                            <Input
                                inputClassName="text-center"
                                className="w-20 text-center outline-0 text-sm flex"
                                value={inputValue}
                                onChange={e => handlePageInput(e.target.value)}
                            />
                            <p className="text-gray3">/ {lastPage}</p>
                            <button onClick={() => currentPage < lastPage && handlePageChange(currentPage + 1)}>
                                <RightArrow />
                            </button>

                            {/* 신규 발주 알림 - 본점 알림(/console/alert)은 전사 공용이라 쓰지 않는다 */}
                            <div className="relative">
                                <button
                                    className="flex flex-col items-center justify-center rounded-xl"
                                    onClick={handleAlarmToggle}
                                >
                                    <Alarm className="w-[24px] h-[24px] z-10" />
                                    {newOrderCount !== 0 && (
                                        <p className="animate-blink bg-error text-white w-[32px] rounded-[20px] text-xs -translate-y-1.5">
                                            {newOrderCount}
                                        </p>
                                    )}
                                </button>
                                {alarmPop && (
                                    <BranchAlarmPop
                                        onClose={() => setAlarmPop(false)}
                                        onSelectOrder={handleOpenDetail}
                                        onShowUnconfirmed={() => handleOrderStatusClick(1)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Title */}
                    <div className="flex justify-between p-3 items-center border-b border-gray4">
                        <div className="flex gap-2 ">
                            <div className="flex items-center gap-1">
                                <p className="text-xs">총</p>
                                <button className="text-sm font-bold text-primary2">
                                    {(listData?.total_count || 0).toLocaleString()}
                                </button>
                            </div>
                            <div className="flex items-center gap-1">
                                <p className="text-xs">선택</p>
                                <button className="text-sm font-bold text-primary2">{selectedIds.length.toLocaleString()}</button>
                            </div>

                            <div className="flex gap-4 items-center">
                                <div className="flex gap-1 items-center relative">
                                    <Button
                                        disabled={selectedIds.length === 0}
                                        onClick={() => setChangeStatus(true)}
                                        size="S"
                                        icon={<SelectIcon />}
                                        iconPosition="right"
                                        className="px-2"
                                    >
                                        일괄 주문상태 변경
                                    </Button>
                                    {changeStatus && (
                                        <div
                                            ref={statusRef}
                                            className="absolute left-0 top-0 p-2 translate-y-9 bg-white border rounded-xl shadow z-10 w-[144px]"
                                        >
                                            {STATUS_CHANGE_OPTIONS.map(option => (
                                                <button
                                                    key={option.value}
                                                    className="block w-full hover:text-primary2 rounded-lg hover:bg-primary4 text-gray2 px-4 py-2 text-center"
                                                    onClick={() => {
                                                        setChangeStatus(false);
                                                        handleStatusSubmit(option.value);
                                                    }}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <Button
                                    disabled={selectedIds.length === 0}
                                    size="S"
                                    className="px-2"
                                    onClick={handlePrintReceipt}
                                >
                                    인수증 출력
                                </Button>

                                <Button
                                    disabled={selectedIds.length === 0}
                                    size="S"
                                    status="error"
                                    className="px-2"
                                    onClick={handleBranchDelete}
                                >
                                    삭제
                                </Button>
                            </div>
                        </div>

                        <div className="flex gap-2 items-center z-10">
                            <button>
                                <Refresh onClick={handleRefresh} className="cursor-pointer" />
                            </button>
                            <SelectBox
                                status="none"
                                className="w-[100px]"
                                options={[
                                    { value: "50", label: "50건씩" },
                                    { value: "100", label: "100건씩" },
                                ]}
                                value={pageSize.toString()}
                                onChange={value => {
                                    setPageSize(Number(value));
                                    resetPaging();
                                }}
                            />
                            <div className="relative">
                                <Filter className="cursor-pointer" onClick={() => setFilterClick(!filterClick)} />
                                {filterClick && (
                                    <div className="absolute top-8 right-0 bg-white z-50 rounded-2xl">
                                        <BranchFilterDetail
                                            filterData={{
                                                start_date: startDate,
                                                end_date: endDate,
                                                order_status: orderStatusList,
                                            }}
                                            onApplyFilter={handleApplyFilter}
                                            onClose={() => setFilterClick(false)}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="w-[240px] flex gap-1">
                                <SelectBox
                                    status="none"
                                    className="min-w-[110px]"
                                    options={SEARCH_OPTIONS}
                                    value={search}
                                    onChange={(value: string) => setSearch(value)}
                                />
                                <Input
                                    placeholder="검색"
                                    status="none"
                                    className=""
                                    value={searchTxt}
                                    onChange={e => {
                                        setSearchTxt(e.target.value);
                                        setInputValue("1");
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <div className="relative">
                                <Button
                                    size="S"
                                    className="px-2"
                                    icon={<SelectIconExcel className={`${excelClick ? "rotate-180" : ""}`} />}
                                    iconPosition="right"
                                    onClick={() => setExcelClick(prev => !prev)}
                                >
                                    엑셀
                                </Button>
                                {excelClick && (
                                    <div className="absolute top-9 right-0 z-50">
                                        <BranchExcel
                                            selectedIds={selectedIds}
                                            startDate={startDate}
                                            endDate={endDate}
                                            orderStatus={orderStatusList}
                                            onClose={() => setExcelClick(false)}
                                            onError={message => addToast({ type: "error", message })}
                                        />
                                    </div>
                                )}
                            </div>
                            {/* 상세 패널 열고 닫기. 열린 탭이 없으면 누를 게 없다 */}
                            <button
                                onClick={() => setDetailOpen(prev => !prev)}
                                disabled={tabs.length === 0}
                                title={detailOpen ? "상세 닫기" : "상세 열기"}
                                className={`border border-gray4 p-1 rounded-lg ${detailOpen ? "rotate-180" : ""} ${
                                    tabs.length === 0 ? "cursor-default opacity-40" : "cursor-pointer"
                                }`}
                            >
                                <ArrowLeft />
                            </button>
                        </div>
                    </div>

                    {/* 목록 */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                        <BranchTable
                            data={listData?.data_list}
                            activeTabId={activeTabId}
                            selectedRow={handleOpenDetail}
                            selectedRows={ids => setSelectedIds(ids)}
                            clearTrigger={clearSelection}
                            onClearComplete={() => setClearSelection(false)}
                            field={value => {
                                const [field, order] = value.split(",");
                                setSortField(field);
                                setSortOrder(order as "ASC" | "DESC");
                            }}
                        />
                    </div>
                </div>

                {/* 상세 패널 */}
                {tabs.length > 0 && detailOpen && (
                    <div className="bg-gray6 overflow-hidden w-[560px] shrink-0 h-screen flex flex-col z-50 shadow-custom">
                        <div className="sticky top-0 z-10 mx-4 overflow-x-auto">
                            <div className="flex pt-4 w-max">
                                {tabs.map(tab => (
                                    <div
                                        key={tab.id}
                                        className={`text-sm flex items-center rounded-t-lg justify-between min-w-[140px] cursor-pointer px-2 py-1.5 ${
                                            activeTabId === tab.id ? "bg-white" : "bg-gray5 text-gray3"
                                        }`}
                                        onClick={() => setActiveTabId(tab.id)}
                                    >
                                        {tab.orderNumber || tab.id}
                                        <button
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleCloseTab(tab.id);
                                            }}
                                        >
                                            <CloseBlackIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {tabs.map(tab => (
                                <div key={tab.id} className={`${activeTabId === tab.id ? "block" : "hidden"} h-full`}>
                                    <BranchOrderDetail
                                        selectedIdx={tab.id}
                                        onOrderNumberChange={orderNumber =>
                                            setTabs(prev =>
                                                prev.map(item =>
                                                    item.id === tab.id ? { ...item, orderNumber } : item,
                                                ),
                                            )
                                        }
                                        onDeleted={() => handleCloseTab(tab.id)}
                                        onRefresh={refetchFn => handleSetRefetch(tab.id, refetchFn)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </BranchLayout>
    );
};

export default OrderList;
